import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Tabs as AntdTabs,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import { updateCompanyCustomizationTabs } from '../state/settings.actions';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import { getEquipmentCustomFields } from '../../projects/projectExport';
import {
  getProjectEquipmentTableColumns,
  updateProjectEquipmentTableColumns,
} from '../../projects/state/projects.actions';

const categoryToEntityMap = {
  Projects: 'Project',
};

const tabStyle = { paddingTop: 7, overflowY: 'auto', height: '90vh' };
const { TabPane } = AntdTabs;

function Tabs() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [categoryToTabMap, setCategoryToTabMap] = useState({});
  const [stateKey, setStateKey] = useState("Project (General)");

  const dispatch = useDispatch();
  const tabs = useSelector((state) => state.settings.tabs);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);

  const hasEquipmentTabEnabled = (category) => (
    (categoryToTabMap[category] ?? []).some((tab) => tab.tabName === 'Equipment' && tab.enabled)
  )

  const getProjectTypeIdForCategory = (category) => {
    const categoryTabs = categoryToTabMap[category];
    return categoryTabs?.[0]?.projectTypeId;
  };

  const projectTypeId = getProjectTypeIdForCategory(stateKey);
  const equipmentTableColumns = useSelector(
    (state) => state.projects.equipmentTableColumns[projectTypeId] ?? [],
  );

  useEffect(() => {
    const categoryMap = {};

    tabs.forEach((tab) => {
      const suffix = ` (${tab.subCategory ?? 'General'})`;
      const relevantCategory = t(categoryToEntityMap[tab.category] ?? tab.category) + suffix;

      if (!categoryMap[relevantCategory]) {
        categoryMap[relevantCategory] = [];
      }
      categoryMap[relevantCategory].push(tab);
    });

    setCategories(Object.keys(categoryMap));
    setCategoryToTabMap(categoryMap);
  }, [tabs]);

  useEffect(() => {
    if (!hasEquipmentTabEnabled(stateKey)) return;
    dispatch(getProjectEquipmentTableColumns(projectTypeId));
  }, [stateKey, projectTypeId]);

  const equipmentTableColumnOptions = getEquipmentCustomFields(equipmentTypes).map((field) => ({
    label: <span><strong>{field.equipmentTypeName}</strong>: {field.title}</span>,
    value: field.id,
    filterLabel: `${field.equipmentTypeName}: ${field.title}`,
  }));

  const onChangeEquipmentColumns = (value) => {
    dispatch(updateProjectEquipmentTableColumns(projectTypeId, {
      fieldIds: value ?? [],
    }));
  }

  const onChange = async (tab, checked) => {
    await dispatch(updateCompanyCustomizationTabs(tab.id, {
      enabled: checked,
    }));
  };

  return (
    <Row
      id="divisions-container"
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <AntdTabs
        tabPosition="left"
        tabBarStyle={{
          marginTop: 15,
          width: 135,
          marginRight: 17,
        }}
        style={{ width: '100%' }}
        activeKey={stateKey}
        onChange={setStateKey}
      >
        { categories.map((category) => (
          <TabPane style={tabStyle} tab={category} key={category}>
            <CompanySettingsCard
              title={`${category} Tabs`}
              style={{
                padding: 0,
                height: 'auto',
              }}
            >
              <CompanyEditRow
                key={category}
                divider={false}
              >
                <Row>
                  {categoryToTabMap[category].map((tab) => (
                    <Col span={8} key={tab.tabName} style={{ marginTop: 5 }}>
                      <OnTraccrSwitch
                        onChange={(checked) => onChange(tab, checked)}
                        value={tab.enabled}
                        title={tab.tabName}
                        useRightView
                      />
                    </Col>
                  ))}
                </Row>
              </CompanyEditRow>
            </CompanySettingsCard>
            {hasEquipmentTabEnabled(category) &&  
              <CompanySettingsCard
                title={`${category} Equipment Tab`}
              >
                <CompanyEditRow
                  divider={false}
                  childrenOnNewLine
                  childColumnStyle={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: 20,
                    gap: 10,
                  }}
                  style={{
                    marginVertical: 10,
                    marginHorizontal: 0,
                  }}
                  textColumnMax='100%'
                  title="Equipment Table Columns"
                  helpText="Select columns from equipment custom fields to show in the project equipment table."
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select columns to show"
                    options={equipmentTableColumnOptions}
                    value={equipmentTableColumns}
                    onChange={onChangeEquipmentColumns}
                    style={{ width: '100%', maxWidth: 480 }}
                    filterOption={(input, option) => {
                      const text = option?.filterLabel ?? '';
                      return text.toLowerCase().includes((input || '').toLowerCase());
                    }}
                  />
                </CompanyEditRow>
              </CompanySettingsCard>
            }
          </TabPane>
        ))}
      </AntdTabs>
    </Row>
  );
}

export default Tabs;
